import {
  cn,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Text,
} from '@kognitos/lattice';

/**
 * Props for the BrowserTask component.
 */
export interface IBrowserTaskProps {
  /** The task title to display. Defaults to "Task". */
  title?: string;
  /** The task description/instructions. Can be a string or an array of paragraphs. */
  description: string | string[];
  /** Additional CSS classes. */
  className?: string;
}

/**
 * BrowserTask displays the task card with title and description for browser automation.
 *
 * Shows the task that the browser automation is working on, with support
 * for multi-paragraph descriptions.
 *
 * @param props - Component props
 * @returns BrowserTask component
 *
 * @example
 * <BrowserTask
 *   description="On Google flights, find a one-way business class flight from Buenos Aires to Amsterdam on the 10th of next month."
 * />
 *
 * @example
 * <BrowserTask
 *   title="Task"
 *   description={[
 *     "On Google flights, find a one-way business class flight from Buenos Aires to Amsterdam on the 10th of next month.",
 *     "Provide the details of the flight with the shortest duration"
 *   ]}
 * />
 */
export function BrowserTask({
  title = 'Task',
  description,
  className,
}: IBrowserTaskProps) {
  const paragraphs = Array.isArray(description) ? description : [description];

  return (
    <Card className={cn('gap-0', className)}>
      <CardHeader className="pb-0">
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {paragraphs.map(paragraph => (
          <Text key={`${paragraph}`} level="small" color="muted">
            {paragraph}
          </Text>
        ))}
      </CardContent>
    </Card>
  );
}
